import { useEffect, useState, useCallback } from 'react';
import { useInViewOnce } from './useInViewOnce';

const MAX_PARALLAX_PX = 11;

/**
 * ورود در ویو + پارالاکس ماوس (فقط md+ و بدون reduced-motion).
 */
export function useCollageMotion() {
  const [containerRef, inView] = useInViewOnce({ once: true, threshold: 0.08, rootMargin: '0px 0px -6% 0px' });
  const [mouseNorm, setMouseNorm] = useState({ x: 0, y: 0 });
  const [reducedMotion, setReducedMotion] = useState(false);
  const [allowPointerFx, setAllowPointerFx] = useState(false);

  useEffect(() => {
    const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mqMd = window.matchMedia('(min-width: 768px)');
    const sync = () => {
      setReducedMotion(mqReduce.matches);
      setAllowPointerFx(!mqReduce.matches && mqMd.matches);
    };
    sync();
    mqReduce.addEventListener('change', sync);
    mqMd.addEventListener('change', sync);
    return () => {
      mqReduce.removeEventListener('change', sync);
      mqMd.removeEventListener('change', sync);
    };
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !inView || !allowPointerFx) return undefined;

    const handleMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / Math.max(r.width, 1) - 0.5;
      const y = (e.clientY - r.top) / Math.max(r.height, 1) - 0.5;
      setMouseNorm({ x: Math.max(-1, Math.min(1, x * 2)), y: Math.max(-1, Math.min(1, y * 2)) });
    };

    const handleLeave = () => setMouseNorm({ x: 0, y: 0 });

    el.addEventListener('mousemove', handleMove);
    el.addEventListener('mouseleave', handleLeave);
    return () => {
      el.removeEventListener('mousemove', handleMove);
      el.removeEventListener('mouseleave', handleLeave);
    };
  }, [containerRef, inView, allowPointerFx]);

  const getParallaxStyle = useCallback(
    (depth) => {
      if (reducedMotion || !allowPointerFx) {
        return undefined;
      }
      const dx = mouseNorm.x * MAX_PARALLAX_PX * depth;
      const dy = mouseNorm.y * MAX_PARALLAX_PX * depth;
      return {
        transform: `translate3d(${dx.toFixed(2)}px, ${dy.toFixed(2)}px, 0)`,
      };
    },
    [allowPointerFx, reducedMotion, mouseNorm.x, mouseNorm.y]
  );

  return { containerRef, inView, getParallaxStyle, reducedMotion };
}
